import { useMemo } from 'react';
import { useLanguage } from './useLanguage';
import { Media } from '../Model/Media/Media';
import { languageState } from '../Contexts/LanguageContext';

const languagePriority = ['de', 'en', 'fr', 'it'];

/**
 * Returns the best matching Media from a Media array based on current language.
 * Works for images, videos, or any localized media array.
 *
 * @example
 * const image = useLocalizedMedia(entity.images);
 * const video = useLocalizedMedia(entity.videos);
 *
 * // Then use the media properties:
 * <img src={image?.large || image?.medium} />
 */
export function useLocalizedMedia(mediaArray: Media[] | undefined | null): Media | undefined {
  const { language } = useLanguage();

  return useMemo(() => {
    if (!mediaArray || mediaArray.length === 0) {
      return undefined;
    }

    const langLower = language.toLowerCase();

    // Try exact language match first
    const exactMatch = mediaArray.find((m) => m.language?.toLowerCase() === langLower);
    if (exactMatch) {
      return exactMatch;
    }

    // Try language priority fallback
    for (const lang of languagePriority) {
      const match = mediaArray.find((m) => m.language?.toLowerCase() === lang);
      if (match) {
        return match;
      }
    }

    // Return first available
    return mediaArray[0];
  }, [mediaArray, language]);
}

/*
 * @deprecated only use in "migration" cases
 */
export function getLocalizedMedia(mediaArray: Media[] | undefined | null): Media | undefined {
  const language = languageState.language;
  if (!mediaArray || mediaArray.length === 0) {
    return undefined;
  }

  const langLower = language.toLowerCase();

  // Try exact language match first
  const exactMatch = mediaArray.find((m) => m.language?.toLowerCase() === langLower);
  if (exactMatch) {
    return exactMatch;
  }

  // Try language priority fallback
  for (const lang of languagePriority) {
    const match = mediaArray.find((m) => m.language?.toLowerCase() === lang);
    if (match) {
      return match;
    }
  }

  // Return first available
  return mediaArray[0];
}

/**
 * Convenience hook specifically for images.
 * Alias for useLocalizedMedia.
 */
export function useLocalizedImage(images: Media[] | undefined | null): Media | undefined {
  return useLocalizedMedia(images?.filter((m) => m.mediaType?.startsWith('image/')));
}

/**
 * Convenience hook specifically for videos.
 * Alias for useLocalizedMedia.
 */
export function useLocalizedVideo(videos: Media[] | undefined | null): Media | undefined {
  return useLocalizedMedia(videos?.filter((m) => m.mediaType?.startsWith('video/')));
}
